import Phaser from 'phaser';
import { EDITOR_PREVIEW_KEY } from './PreviewScene';

// Cards that have separate images per side
export const SIDE_VARIANT_CARDS = new Set([
  'counter-attack', 'armor-assault', 'direct-from-hq', 'infantry-assault', 'move-out',
]);

// All unique card IDs in the deck
const GENERIC_CARD_IDS = [
  'probe-left', 'probe-center', 'probe-right',
  'attack-left', 'attack-center', 'attack-right',
  'assault-left', 'assault-center', 'assault-right',
  'recon-left', 'recon-center', 'recon-right',
  'pincer-move', 'recon-in-force', 'general-advance',
  'firefight', 'close-assault', 'behind-enemy-lines',
  'ambush', 'artillery-bombard', 'barrage',
  'medics-and-mechanics', 'dig-in', 'their-finest-hour', 'air-power',
];

export function cardTextureKey(cardId: string, side: 'allies' | 'axis'): string {
  return SIDE_VARIANT_CARDS.has(cardId) ? `card-${cardId}-${side}` : `card-${cardId}`;
}

// All texture keys we expect to exist
const ALL_CARD_KEYS: string[] = [
  ...GENERIC_CARD_IDS.map(id => `card-${id}`),
  ...[...SIDE_VARIANT_CARDS].flatMap(id => [`card-${id}-allies`, `card-${id}-axis`]),
];

export class PreloadScene extends Phaser.Scene {
  constructor() { super({ key: 'PreloadScene' }); }

  preload(): void {
    const W = 1440, H = 768;

    // Simple loading bar
    const barBg  = this.add.rectangle(W / 2, H / 2, 400, 16, 0x334433);
    const bar    = this.add.rectangle(W / 2 - 200, H / 2, 0, 12, 0xd4af37).setOrigin(0, 0.5);
    const label  = this.add.text(W / 2, H / 2 + 24, 'Loading…', {
      fontSize: '13px', color: '#aaaaaa',
    }).setOrigin(0.5);
    void barBg;

    this.load.on('progress', (v: number) => {
      bar.width = 400 * v;
      label.setText(`Loading… ${Math.round(v * 100)}%`);
    });

    // Generic cards
    for (const id of GENERIC_CARD_IDS) {
      this.load.image(`card-${id}`, `assets/cards/${id}.jpeg`);
    }

    // Side-variant cards
    for (const id of SIDE_VARIANT_CARDS) {
      this.load.image(`card-${id}-allies`, `assets/cards/${id}-allies.jpeg`);
      this.load.image(`card-${id}-axis`,   `assets/cards/${id}-axis.jpeg`);
    }

    // Card back
    this.load.image('card-back', 'assets/cards/card-back.jpeg');

    // Commissar token portrait (Soviet house rule)
    this.load.image('commissar-token', 'assets/commisar.jpg');
  }

  create(): void {
    // For any card whose image failed to load or rendered incorrectly (wrong dimensions),
    // generate a simple dark-green placeholder so the hand never shows a black void.
    for (const key of ALL_CARD_KEYS) {
      const tex = this.textures.get(key);
      const src = tex.source[0];
      const missing = tex.key === '__MISSING' || !src || src.width !== 210 || src.height < 310 || src.height > 340;
      if (missing) {
        this.makePlaceholder(key);
      }
    }

    // The editor's "Preview" button opens a new tab and stashes the scenario
    // JSON here before navigating — skip straight to the read-only board preview.
    if (localStorage.getItem(EDITOR_PREVIEW_KEY)) {
      this.scene.start('PreviewScene');
    } else {
      this.scene.start('LobbyScene');
    }
  }

  private makePlaceholder(key: string): void {
    // Derive a readable label from the texture key
    const label = key
      .replace(/^card-/, '')
      .replace(/-(allies|axis)$/, '')
      .replace(/-/g, ' ')
      .toUpperCase();

    const W = 210, H = 325;
    const gfx  = this.make.graphics({} as any);
    gfx.fillStyle(0x1a3322, 1);
    gfx.fillRoundedRect(0, 0, W, H, 10);
    gfx.lineStyle(2, 0xd4af37, 1);
    gfx.strokeRoundedRect(1, 1, W - 2, H - 2, 10);

    const txt = this.add.text(W / 2, H / 2, label, {
      fontSize: '16px', color: '#d4af37', fontStyle: 'bold', align: 'center', wordWrap: { width: 180 },
    }).setOrigin(0.5);

    const rt = this.add.renderTexture(0, 0, W, H);
    rt.draw(gfx, 0, 0);
    rt.draw(txt, 0, 0);
    rt.saveTexture(key);

    gfx.destroy();
    txt.destroy();
    rt.destroy();
  }
}
